import { useCallback, useEffect, useMemo, useState } from "react";
import "@/App.css";
import { ARTISTS, slugify } from "@/data/artists";
import useLenis from "@/hooks/useLenis";
import { playTunnelSwell, isSoundMuted, setSoundMuted } from "@/utils/swell";
import SoundToggle from "@/components/SoundToggle";
import Threshold from "@/components/Threshold";
import Hall from "@/components/Hall";
import HueTunnel from "@/components/HueTunnel";
import ArtistWorld from "@/components/ArtistWorld";
import PieceModal from "@/components/PieceModal";
import Curate from "@/components/Curate";
import About from "@/components/About";
import Journal from "@/components/Journal";

const API = process.env.REACT_APP_BACKEND_URL;

const parseHash = () => {
  const m = window.location.hash.match(/^#\/world\/([a-z0-9-]+)$/);
  if (!m) return null;
  const i = ARTISTS.findIndex((a) => slugify(a.name) === m[1]);
  return i >= 0 ? i : null;
};

const isCurateHash = () => window.location.hash === "#/curate";
const isAboutHash = () => window.location.hash === "#/about";
const isJournalHash = () => window.location.hash === "#/journal";

function App() {
  const [entered, setEntered] = useState(() => parseHash() !== null || window.location.hash === "#hall" || isCurateHash() || isAboutHash() || isJournalHash());
  const [artistIndex, setArtistIndex] = useState(() => parseHash());
  const [curate, setCurate] = useState(() => isCurateHash());
  const [about, setAbout] = useState(() => isAboutHash());
  const [journal, setJournal] = useState(() => isJournalHash());
  const [modalArtist, setModalArtist] = useState(null);
  const [tunnelHue, setTunnelHue] = useState(null);
  const [muted, setMuted] = useState(isSoundMuted());
  const [artworkMap, setArtworkMap] = useState({});
  useLenis();

  useEffect(() => {
    fetch(`${API}/api/artwork`)
      .then((r) => r.json())
      .then((d) => {
        const map = {};
        (d.items || []).forEach((it) => {
          map[it.slug] = it;
        });
        setArtworkMap(map);
      })
      .catch(() => {});
  }, []);

  const artists = useMemo(
    () =>
      ARTISTS.map((a) => {
        const slug = slugify(a.name);
        const apply = (piece, s) => {
          const m = artworkMap[s];
          if (!m) return piece;
          return {
            ...piece,
            image: m.image ? API + m.image : piece.image,
            video: m.video ? API + m.video : piece.video,
          };
        };
        const merged = { ...a, piece: apply(a.piece, slug) };
        if (a.periods) {
          merged.periods = a.periods.map((p) => ({ ...p, piece: apply(p.piece, `${slug}-${p.slug}`) }));
        }
        if (a.pieces) {
          merged.pieces = a.pieces.map((p, i) => apply(p, `${slug}-piece-${i + 1}`));
        }
        merged.photos = a.hidePhotos
          ? []
          : [`${slug}-photo-1`, `${slug}-photo-2`]
              .map((s) => artworkMap[s])
              .filter((m) => m && m.image)
              .map((m) => API + m.image);
        return merged;
      }),
    [artworkMap]
  );

  useEffect(() => {
    const onMove = (e) => {
      document.documentElement.style.setProperty("--mx", ((e.clientX / window.innerWidth) * 100).toFixed(1) + "%");
      document.documentElement.style.setProperty("--my", ((e.clientY / window.innerHeight) * 100).toFixed(1) + "%");
    };
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        document.documentElement.style.setProperty("--sa", ((window.scrollY * 0.06) % 360).toFixed(2) + "deg");
        ticking = false;
      });
    };
    document.addEventListener("mousemove", onMove);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("mousemove", onMove);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    const onHash = () => {
      if (isCurateHash()) {
        setEntered(true);
        setCurate(true);
        setAbout(false);
        setJournal(false);
        setArtistIndex(null);
        return;
      }
      setCurate(false);
      if (isAboutHash()) {
        setEntered(true);
        setAbout(true);
        setJournal(false);
        setArtistIndex(null);
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true, force: true });
        window.scrollTo(0, 0);
        return;
      }
      setAbout(false);
      if (isJournalHash()) {
        setEntered(true);
        setJournal(true);
        setArtistIndex(null);
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true, force: true });
        window.scrollTo(0, 0);
        return;
      }
      setJournal(false);
      const idx = parseHash();
      if (idx !== null) {
        setEntered(true);
        setArtistIndex(idx);
      } else {
        setArtistIndex(null);
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true, force: true });
        window.scrollTo(0, 0);
      }
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    const lenis = window.__lenis;
    if (!lenis) return;
    if (modalArtist) lenis.stop();
    else lenis.start();
  }, [modalArtist]);

  const closeModal = useCallback(() => setModalArtist(null), []);

  const openArtist = (i) => {
    if (tunnelHue !== null) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      window.location.hash = `/world/${slugify(ARTISTS[i].name)}`;
      return;
    }
    setTunnelHue(ARTISTS[i].hue);
    playTunnelSwell(ARTISTS[i].hue);
    setTimeout(() => {
      window.location.hash = `/world/${slugify(ARTISTS[i].name)}`;
      setTunnelHue(null);
    }, 1150);
  };

  return (
    <div className="App">
      {!entered ? (
        <Threshold
          onEnter={() => {
            window.history.replaceState(null, "", "#hall");
            setEntered(true);
          }}
        />
      ) : curate ? (
        <Curate artists={artists} />
      ) : about ? (
        <About
          onBack={() => {
            window.location.hash = "hall";
          }}
        />
      ) : journal ? (
        <Journal
          onBack={() => {
            window.location.hash = "hall";
          }}
        />
      ) : artistIndex === null ? (
        <Hall artists={artists} onOpen={openArtist} />
      ) : (
        <ArtistWorld
          artist={artists[artistIndex]}
          index={artistIndex}
          total={artists.length}
          onBack={() => {
            window.location.hash = "hall";
          }}
          onNext={() => {
            window.location.hash = `/world/${slugify(artists[(artistIndex + 1) % artists.length].name)}`;
          }}
          onViewPiece={(payload) => setModalArtist(payload || artists[artistIndex])}
        />
      )}
      {tunnelHue !== null && <HueTunnel hue={tunnelHue} />}
      <PieceModal artist={modalArtist} onClose={closeModal} />
      <SoundToggle
        muted={muted}
        onToggle={() => {
          const next = !muted;
          setSoundMuted(next);
          setMuted(next);
        }}
      />
    </div>
  );
}

export default App;
